import { FC, MouseEvent, useEffect, useRef, useState } from "react";
import { Control, useController } from "react-hook-form";
import styled from "styled-components";

import { Input } from "@/components/Forms";
import Tag from "@/components/Tag";
import Text from "@/components/Text";
import useDebounce from "@/hooks/useDebounce";
import { PaginatedResponse, VideoTipResource } from "@/types/resources";
import { api } from "@/utils/api";
import { splitMatchedText } from "@/utils/helpers";
import { FormDataT } from "./DistributionScheduleTipsModal";
import ValidationMessage from "@/components/Forms/ValidationMessage";

const Autocomplete = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  padding: 8px 4px;
  max-height: 368px;
  overflow-y: auto;
  left: 0;
  width: 100%;
  z-index: 1;
`;

const InputWrap = styled.div`
  position: relative;
`;

const AutocompleteButton = styled.button`
  padding: 9px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;
  line-height: 22px;
  width: 100%;
  text-align: left;
  border-radius: 4px;
  outline: none;
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    ${Text} {
      &:first-child {
        color: ${({ theme }) => theme.colors.black};
      }
    }
  }
  ${Text} {
    transition: color ease-out 0.2s;
    > span {
      color: ${({ theme }) => theme.colors.purple};
    }
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

interface Props {
  control: Control<FormDataT>;
}

const DistributionSchedulesTipsField: FC<Props> = ({ control }) => {
  const [suggestions, setSuggestions] = useState<VideoTipResource[]>([]);

  const [title, setTitle] = useState("");

  const {
    field: { onChange, value: attachments },
    fieldState: { error },
  } = useController({
    name: "tips",
    control,
  });

  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedTip = useDebounce(title, 400);

  const filteredSuggestions = suggestions.filter(
    (s) => !attachments.map((a) => a.id).includes(s.id)
  );

  useEffect(() => {
    if (debouncedTip.length < 2) {
      return setSuggestions([]);
    }

    api
      .get<never, PaginatedResponse<VideoTipResource>>("/v1/video-tips", {
        params: {
          "filter[search]": debouncedTip,
          per_page: 100,
        },
      })
      .then((suggestions) => setSuggestions(suggestions.data));
  }, [debouncedTip]);

  const addAttachment = (tipToAdd: {
    name: string;
    external_id: string;
    id: number;
  }) => {
    if (!attachments.find((a) => a.id === tipToAdd.id)) {
      onChange([
        ...attachments,
        {
          name: tipToAdd.name,
          external_id: tipToAdd.external_id,
          id: tipToAdd.id,
        },
      ]);
    }

    setTitle("");
    setSuggestions([]);
  };

  const onSuggestionClick = (suggestion: {
    name: string;
    external_id: string;
    id: number;
  }) => {
    addAttachment(suggestion);
    inputRef.current?.focus();
  };

  const onAttachmentRemove = (tipToRemove: number) => {
    onChange(attachments.filter((t) => t.id !== tipToRemove));
  };

  return (
    <>
      <InputWrap>
        <Input
          placeholder="Type tip name or ID"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          ref={inputRef}
        />
        {!!filteredSuggestions.length && (
          <Autocomplete>
            {filteredSuggestions.map((s) => (
              <AutocompleteButton
                key={s.external_id}
                type="button"
                as="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  onSuggestionClick({
                    name: s.name,
                    external_id: s.external_id,
                    id: s.id,
                  });
                }}
              >
                <Text
                  $color="gray"
                  $type="captionSemibold"
                  dangerouslySetInnerHTML={{
                    __html: splitMatchedText(s.external_id, title),
                  }}
                />
              </AutocompleteButton>
            ))}
          </Autocomplete>
        )}
      </InputWrap>
      {!!error && <ValidationMessage>{error.message}</ValidationMessage>}
      {!!attachments?.length && (
        <Tags>
          {attachments.map((t) => (
            <Tag
              key={t.id}
              onDelete={(e) => {
                e.stopPropagation();
                onAttachmentRemove(t.id);
              }}
            >
              {t.external_id}
            </Tag>
          ))}
        </Tags>
      )}
    </>
  );
};

export default DistributionSchedulesTipsField;
